import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  getPublicBookingPresentation,
  getPublicMeetingUrlLabel,
  getSafePublicMeetingUrl,
  isConfirmedPublicBooking,
  mergeBookingPendingState,
  normalizeBookingNavigationState
} from "../../utils/publicBookingStatus";

const API_URL = window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
  : window.location.origin;

const DISPLAY_TIMEZONE = "Asia/Tokyo";

function formatDateTime(value) {
  if (!value) return "読み込み中";
  return new Date(value).toLocaleString("ja-JP", { timeZone: DISPLAY_TIMEZONE });
}

export default function PublicBookingCompletePage() {
  const { publicId, appointmentPublicId } = useParams();
  const location = useLocation();
  const [appointment, setAppointment] = useState(null);
  const [pendingState, setPendingState] = useState(() => normalizeBookingNavigationState(location.state));
  const [error, setError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchAppointment = useCallback(async () => {
    setIsRefreshing(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/public/booking/appointments/${encodeURIComponent(appointmentPublicId)}`);
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || "予約情報の取得に失敗しました。");
      }
      setAppointment(json.appointment);
      setPendingState((current) => mergeBookingPendingState(current, json));
    } catch (fetchError) {
      setError(fetchError.message || "予約情報の取得に失敗しました。");
    } finally {
      setIsRefreshing(false);
    }
  }, [appointmentPublicId]);

  useEffect(() => {
    fetchAppointment();
  }, [fetchAppointment]);

  const presentation = getPublicBookingPresentation({
    appointment,
    bookingPending: pendingState.bookingPending
  });
  const bookingPending = presentation.key === "pending";
  const bookingConfirmed = isConfirmedPublicBooking(appointment);
  const meetingUrlLabel = getPublicMeetingUrlLabel({ appointment, bookingPending });
  const safeMeetingUrl = bookingConfirmed ? getSafePublicMeetingUrl(appointment?.meeting_url) : "";
  const linkState = useMemo(() => normalizeBookingNavigationState({
    bookingPending,
    warning: pendingState.warning
  }), [bookingPending, pendingState.warning]);
  const eyebrowTone = presentation.tone === "success"
    ? "text-green-600"
    : presentation.tone === "pending"
      ? "text-amber-600"
      : presentation.tone === "error"
        ? "text-red-600"
        : "text-gray-500";

  return (
    <div className="min-h-screen bg-gray-50 px-3 py-6 sm:px-4 sm:py-10">
      <div className="mx-auto max-w-2xl rounded-2xl sm:rounded-3xl bg-white p-5 sm:p-8 shadow-sm">
        <p className={`text-sm font-semibold ${eyebrowTone}`}>{presentation.eyebrow}</p>
        <h1 className="mt-2 text-2xl sm:text-3xl font-bold text-gray-900">
          {presentation.title}
        </h1>
        <p className="mt-4 text-sm text-gray-600">{presentation.description}</p>

        {bookingPending ? (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm leading-6 text-amber-900" role="status">
            <p className="font-bold">Zoom会議URLはまだ準備できていません。</p>
            {pendingState.warning ? <p className="mt-2">{pendingState.warning}</p> : null}
            <p className="mt-2">確認メールはまだ送信されていません。予約フォームを再送信する必要はありません。</p>
            <p className="mt-2">主催者は予約管理画面からZoom URLの発行を再試行できます。しばらくしても変わらない場合は、予約IDを添えて案内元へご連絡ください。</p>
          </div>
        ) : null}

        {error ? (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <dl className="mt-6 space-y-4 text-sm text-gray-600">
          <div>
            <dt className="font-semibold text-gray-900">予約リンクID</dt>
            <dd className="font-mono break-all">{publicId}</dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-900">予約ID</dt>
            <dd className="font-mono break-all">{appointmentPublicId}</dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-900">日時</dt>
            <dd>{formatDateTime(appointment?.started_at)}</dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-900">予約者</dt>
            <dd>{appointment?.invitee_name || "読み込み中"}</dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-900">会議 URL</dt>
            <dd className="break-all">
              {safeMeetingUrl ? (
                <a href={safeMeetingUrl} target="_blank" rel="noreferrer" className="font-bold text-blue-600 hover:text-blue-700">
                  {meetingUrlLabel}
                </a>
              ) : meetingUrlLabel}
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-900">状態</dt>
            <dd>{presentation.statusLabel}</dd>
          </div>
        </dl>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to={`/public/booking/${publicId}/manage/${appointmentPublicId}`}
            state={linkState}
            className="rounded-2xl bg-gray-900 px-4 py-3 text-center text-sm font-bold text-white transition hover:bg-gray-800"
          >
            予約内容を確認する
          </Link>
          {bookingPending ? (
            <button
              type="button"
              onClick={fetchAppointment}
              disabled={isRefreshing}
              className="rounded-2xl border border-amber-300 px-4 py-3 text-center text-sm font-bold text-amber-800 transition hover:bg-amber-50 disabled:opacity-60"
            >
              {isRefreshing ? "確認中..." : "状態を再確認"}
            </button>
          ) : bookingConfirmed ? (
            <Link
              to={`/public/booking/${publicId}/reschedule/${appointmentPublicId}`}
              className="rounded-2xl border border-gray-200 px-4 py-3 text-center text-sm font-bold text-gray-700 transition hover:bg-gray-50"
            >
              日程変更する
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}
